import React from 'react';
import { Dynamo } from '../../../primary-record-types';
import { getLimitMessage, useMedicalProfileUsageMetric } from '../../../hooks/api/useMedicalProfileUsageMetric';
import SkeletonLoadingContainer from '../../v2BaseComponents/SkeletonLoadingContainer';
import { useNestedNavigation } from '../../../utils/appNavigationNames';
import Stack, { ContentStack } from '../../v2BaseComponents/Stack';
import Link from '../../v2BaseComponents/Link';
import { View } from 'react-native';
import { useAppType } from '../../../hooks/useAppType';
import { useIsProfileOwner } from '../../v2BaseComponents/Permissions/hooks';
import { usePromptButton } from '../PromptButton';
import { getIntl } from '../../../utils/intl';
import Text from '../../v2BaseComponents/Text';

const intl = getIntl('MedicalProfileUsageIndicator', {
    nonProfileOwnerPromptTitle: 'Free Tier Limit',
    professionalPromptContent:
        "Your Organization is using the Free Tier and your client's profile have usage limits.  Upgrade to a paid plan to remove these limits.",
    collaboratorPromptContent:
        "This profile owner's is using the Free Tier and has usage limits.  The owner can upgrade to a paid plan to remove these limits.",
});

export type MedicalProfileUsageIndicatorOwnProps = {
    metricType: Dynamo.MedicalProfileUsageMetricType;
    medicalProfileId: string;
};

export type MedicalProfileUsageIndicatorProps = MedicalProfileUsageIndicatorOwnProps;

const MedicalProfileUsageIndicator: React.FC<MedicalProfileUsageIndicatorProps> = ({
    metricType,
    medicalProfileId,
}) => {
    const profileOwner = useIsProfileOwner(medicalProfileId);
    const isProfessionalApp = useAppType() === 'professional';
    const { nestedAccountNavigation } = useNestedNavigation();
    const usageResult = useMedicalProfileUsageMetric({
        medicalProfileId,
        metric: metricType,
    });

    const nonProfileOwnerPromptButton = usePromptButton({
        promptTitle: intl('nonProfileOwnerPromptTitle'),
        renderPromptContents: () => (
            <ContentStack>
                <Text variant="body-1">
                    {isProfessionalApp ? intl('professionalPromptContent') : intl('collaboratorPromptContent')}
                </Text>
            </ContentStack>
        ),
    });

    const limitOnPressHandler = () => {
        if (isProfessionalApp || !profileOwner) {
            nonProfileOwnerPromptButton.onPress();
        } else {
            nestedAccountNavigation('subscription', undefined);
        }
    };

    if (usageResult.allowed === 'unlimited')
        return (
            <View style={{ width: 0, height: 0 }} collapsable={false} testID={'profile-limit-indicator-not-limited'} />
        );

    const content = (
        <Stack horizontal space={2} testID={'profile-limit-indicator-limited'}>
            <Link variant="caption-1" onPress={limitOnPressHandler}>
                {usageResult.hasReachedLimit ? '⚠️' : '✅'}
            </Link>
            <Link variant="caption-1" onPress={limitOnPressHandler}>
                {getLimitMessage({ used: usageResult.used, allowed: usageResult.allowed, metric: metricType })}
            </Link>
        </Stack>
    );

    return (
        <>
            <SkeletonLoadingContainer isLoading={usageResult.isLoadingMetric}>{content}</SkeletonLoadingContainer>
            {nonProfileOwnerPromptButton.Prompt}
        </>
    );
};

export default React.memo(MedicalProfileUsageIndicator);
